import {Alert, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {ApiError} from "@variocube/app-ui";
import React, {useEffect} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {useAuth} from "react-oidc-context";
import {B2Site} from "../../data/client.gen";
import {AdmissionErrorCode, useSiteApi} from "../../data/sites";
import {getZoneDateTimeNow} from "../../dates-utils";
import {useLocalization} from "../../i18n";
import {findActiveBookingOnSite} from "./find-active-booking-on-site";

const SUCCESS_TIMEOUT_MS = 5000;

export function OpenEntranceCta({site}: { site: B2Site }) {
	const {t} = useLocalization();
	const auth = useAuth();
	const {openAdmission, getMyBookings} = useSiteApi();

	// Computed server-side at getSite time (B2Site.currentlyOpen). The server's OUTSIDE_HOURS
	// error remains the safety net at click time, so a stale value across a long-open page
	// session only ever costs one rejected click.
	const accessibleNow = site.currentlyOpen;

	const needsBookingCheck = auth.isAuthenticated && site.requireActiveBookingForAdmission;

	const {result: activeBooking, loading: bookingsLoading} = useAsync(
		async () => {
			if (!needsBookingCheck) return undefined;
			// Filter server-side: only this account's bookings on this site whose timeframe contains now.
			// The findActiveBookingOnSite call below stays — it handles the Day-unit grace period the
			// server-side activeOn Specification doesn't model.
			const bookings = await getMyBookings({siteId: site.id, activeAt: new Date().toISOString()});
			return findActiveBookingOnSite(bookings, site.id, getZoneDateTimeNow(site.zoneId ?? "UTC"));
		},
		[needsBookingCheck, site.id, site.zoneId],
	);

	const {loading, error, result, execute, reset} = useAsyncCallback(async () => {
		await openAdmission(site.id);
		return true;
	});

	useEffect(() => {
		if (!result) return;
		const timer = window.setTimeout(reset, SUCCESS_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [result, reset]);

	if (!site.admissionAvailable) {
		return (
			<Alert severity="info">
				<Typography variant="body2">{t("storefront.admission.noConfig")}</Typography>
			</Alert>
		);
	}

	if (!accessibleNow) {
		return (
			<Alert severity="warning">
				<Typography variant="body2">{t("storefront.admission.outsideHours")}</Typography>
			</Alert>
		);
	}

	if (!auth.isAuthenticated) {
		return (
			<Button
				fullWidth
				size="large"
				variant="contained"
				color="primary"
				disabled={auth.isLoading}
				onClick={() => auth.signinRedirect({redirect_uri: window.location.href})}
			>
				{t("storefront.admission.signInAndOpen")}
			</Button>
		);
	}

	if (needsBookingCheck && bookingsLoading) {
		return (
			<Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
				<CircularProgress size={20} />
			</Stack>
		);
	}

	if (needsBookingCheck && !activeBooking) {
		return (
			<Alert severity="info">
				<Typography variant="body2">{t("storefront.admission.bookingRequired")}</Typography>
			</Alert>
		);
	}

	return (
		<Stack direction="column" gap={2}>
			<Button
				fullWidth
				size="large"
				variant="contained"
				color="primary"
				disabled={loading}
				onClick={() => execute()}
			>
				{t("storefront.admission.openButton")}
			</Button>
			{(error || result) && (
				<Alert severity={error ? "error" : "success"}>
					<Typography variant="body2">{renderMessage()}</Typography>
				</Alert>
			)}
		</Stack>
	);

	function renderMessage() {
		if (error) {
			if (error instanceof ApiError) {
				switch (error.code as AdmissionErrorCode | undefined) {
					case "BOOKING_REQUIRED":
						return t("storefront.admission.error.bookingRequired");
					case "OUTSIDE_HOURS":
						return t("storefront.admission.error.outsideHours");
					case "RATE_LIMITED":
						return t("storefront.admission.error.rateLimited");
				}
			}
			return t("storefront.admission.error.generic");
		}
		return t("storefront.admission.success");
	}
}
